// Friday reports with their state (screen 3's "reports list"). `kpi` only: mounted behind the
// company view's `useFeature("kpi")`. Client viewers get sent reports only (server rule).
import {
  Badge,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { currentWeek, weekRangeLabel } from "../../focus/weeks";
import type { CompanyAccess } from "../company";
import { formatDateTime, REPORT_STATE_LABELS } from "../format";
import { reportsQuery } from "../queries";
import { REPORT_STATE_VARIANT } from "../reports/report-state";
import { LoadError, Loading, LoadMore, Nothing } from "../screen-parts";

export function ReportsList({ api, company }: { api: Api; company: CompanyAccess }) {
  const reports = useInfiniteQuery(reportsQuery(api, company.id));
  if (reports.isPending) return <Loading label="Loading reports" />;
  if (reports.isError) return <LoadError what="Reports" error={reports.error} />;
  const rows = reports.data.pages.flatMap((p) => p.data);
  if (!rows.length)
    return (
      <Nothing title="No reports yet">
        A Friday report is drafted from the week's scorecard;{" "}
        <Link
          to="/c/$slug/kpis/reports/$week"
          params={{ slug: company.slug, week: currentWeek() }}
          search={keepFocus}
          className="text-primary-text hover:underline"
        >
          open this week's
        </Link>
        .
      </Nothing>
    );
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <Table>
        <TableCaption>Friday reports, newest first.</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Week</TableHead>
            <TableHead scope="col">State</TableHead>
            <TableHead scope="col">Generated</TableHead>
            <TableHead scope="col">Sent</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableHead scope="row" className="font-normal">
                <Link
                  to="/c/$slug/kpis/reports/$week"
                  params={{ slug: company.slug, week: r.week_key }}
                  search={(prev) => keepFocus(prev as Record<string, unknown>)}
                  className="font-medium underline-offset-4 hover:underline"
                >
                  {r.week_key}
                </Link>
                <span className="ml-2 text-xs text-muted-foreground">
                  {weekRangeLabel(r.week_key)}
                </span>
              </TableHead>
              <TableCell>
                <Badge variant={REPORT_STATE_VARIANT[r.state]}>
                  {REPORT_STATE_LABELS[r.state]}
                </Badge>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {r.generated_at ? formatDateTime(r.generated_at) : "—"}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {r.sent_at ? formatDateTime(r.sent_at) : "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <LoadMore
        what="reports"
        hasMore={reports.hasNextPage}
        loading={reports.isFetchingNextPage}
        onMore={() => void reports.fetchNextPage()}
      />
    </div>
  );
}
